import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  textbookRequestApi,
  type CreateTextbookRequestBody,
  type UpdateTextbookRequestStatusBody,
} from '@/api/textbookRequests'
import { getApiErrorMessage } from '@/utils/apiError'

export const textbookRequestKeys = {
  studentStatus: (userId: number) => ['textbookRequests', 'student', userId] as const,
  alerts: () => ['textbookRequests', 'alerts'] as const,
  catalog: (curriculumId: number) => ['textbookRequests', 'catalog', curriculumId] as const,
}

export function useStudentTextbookStatus(userId: number) {
  return useQuery({
    queryKey: textbookRequestKeys.studentStatus(userId),
    queryFn: async () => (await textbookRequestApi.getStudentStatus(userId)).data.data,
    enabled: Number.isFinite(userId) && userId > 0,
  })
}

export function useTextbookAlerts() {
  return useQuery({
    queryKey: textbookRequestKeys.alerts(),
    queryFn: async () => (await textbookRequestApi.getAlerts()).data.data ?? [],
    staleTime: 60_000,
  })
}

export function useTextbookCatalog(curriculumId: number | null) {
  return useQuery({
    queryKey: textbookRequestKeys.catalog(curriculumId ?? 0),
    queryFn: async () => {
      if (curriculumId == null) return []
      return (await textbookRequestApi.getCatalog(curriculumId)).data.data ?? []
    },
    enabled: curriculumId != null && curriculumId > 0,
  })
}

export function useCreateTextbookRequest(userId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: (body: CreateTextbookRequestBody) => textbookRequestApi.create(userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: textbookRequestKeys.studentStatus(userId) })
      qc.invalidateQueries({ queryKey: textbookRequestKeys.alerts() })
      toast.success(t('admin:students.detail.textbookRequest.toast.created'))
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}

export function useUpdateTextbookRequestStatus(userId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: ({
      requestId,
      body,
    }: {
      requestId: number
      body: UpdateTextbookRequestStatusBody
    }) => textbookRequestApi.updateStatus(requestId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: textbookRequestKeys.studentStatus(userId) })
      qc.invalidateQueries({ queryKey: textbookRequestKeys.alerts() })
      toast.success(t('admin:students.detail.textbookRequest.toast.statusUpdated'))
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}
